import React, { useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../App';
import './Register.css';

/* Two-step restaurant registration:
   1) Restaurant identity (name, code, branch)
   2) Owner account (name, username, password, confirm)
   The form keeps everything in one state object; navigation between
   steps validates only the current step's fields. */
export default function Register() {
  const { login } = useAuth();
  const navigate  = useNavigate();

  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    restaurant_name: '', restaurant_code: '', branch: '',
    owner_name: '', username: '', password: '', confirm: '',
  });
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Password strength: 0..4 (length + variety). Used only for the meter.
  const pwStrength = useMemo(() => {
    const p = form.password || '';
    let s = 0;
    if (p.length >= 6)  s++;
    if (p.length >= 10) s++;
    if (/[A-Z]/.test(p) && /[a-z]/.test(p)) s++;
    if (/[0-9]/.test(p) && /[^A-Za-z0-9]/.test(p)) s++;
    return s;
  }, [form.password]);

  const step1Valid = form.restaurant_name.trim().length >= 2 &&
                     form.restaurant_code.trim().length >= 3;
  const step2Valid = form.owner_name.trim().length >= 2 &&
                     form.username.trim().length >= 3 &&
                     form.password.length >= 6 &&
                     form.password === form.confirm;

  const goNext = () => {
    setError('');
    if (!step1Valid) {
      setError('Restaurant name and code are required (code: at least 3 characters).');
      return;
    }
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!step2Valid) {
      if (form.password.length < 6)         { setError('Password must be at least 6 characters.'); return; }
      if (form.password !== form.confirm)   { setError('Passwords do not match.');                 return; }
      setError('Please complete every required field.');
      return;
    }
    setLoading(true);
    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const res = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          restaurant_name: form.restaurant_name.trim(),
          restaurant_code: form.restaurant_code.trim(),
          branch:          form.branch.trim(),
          owner_name:      form.owner_name.trim(),
          username:        form.username.trim(),
          password:        form.password,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Registration failed');
      login(data.user, data.token);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rg-page">
      {/* ═════════ Left brand panel ═════════ */}
      <aside className="rg-brand">
        <div className="rg-brand-content">
          <div className="rg-brand-mark">
            <svg viewBox="0 0 60 60" width="44" height="44" aria-hidden="true">
              <rect width="60" height="60" rx="10" fill="#D4A537"/>
              <text x="30" y="40" textAnchor="middle" fontFamily="Newsreader, Georgia, serif"
                    fontStyle="italic" fontWeight="600" fontSize="32" fill="#08122a">VP</text>
            </svg>
            <div>
              <div className="rg-brand-name">Vyapar <em>Pro</em></div>
              <div className="rg-brand-nepali">व्यापार Pro</div>
            </div>
          </div>

          <h2 className="rg-brand-headline">
            Run your restaurant <em>like clockwork.</em>
          </h2>
          <p className="rg-brand-sub">
            Track orders, payments, costs, and the live floor — all from one
            calm screen. Your team logs in with their own account; everything
            else stays in your hands.
          </p>

          <ul className="rg-features">
            {[
              { t: 'Live floor map', s: '2D + 3D walk view, drag tables freely' },
              { t: 'Cash tracking',  s: 'Daily cash-out, profit, inventory snapshots' },
              { t: 'Self-order QR',  s: 'Customers add items from their phone' },
              { t: 'AI reports',     s: 'Plain-language daily summary in your inbox' },
            ].map(f => (
              <li key={f.t}>
                <span className="rg-feature-tick" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none"
                       stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5"/>
                  </svg>
                </span>
                <div>
                  <div className="rg-feature-t">{f.t}</div>
                  <div className="rg-feature-s">{f.s}</div>
                </div>
              </li>
            ))}
          </ul>

          <div className="rg-brand-footnote">
            <span className="rg-dot" /> Trusted by Himalayan kitchens.
          </div>
        </div>
      </aside>

      {/* ═════════ Right form panel ═════════ */}
      <main className="rg-form-panel">
        <div className="rg-form-wrap">
          <div className="rg-progress">
            <div className={`rg-progress-step ${step >= 1 ? 'is-on' : ''} ${step > 1 ? 'is-done' : ''}`}>
              <span className="rg-progress-num">{step > 1 ? (
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
              ) : '1'}</span>
              <span className="rg-progress-label">Restaurant</span>
            </div>
            <div className={`rg-progress-bar ${step >= 2 ? 'is-on' : ''}`} />
            <div className={`rg-progress-step ${step >= 2 ? 'is-on' : ''}`}>
              <span className="rg-progress-num">2</span>
              <span className="rg-progress-label">Owner Account</span>
            </div>
          </div>

          <header className="rg-form-head">
            {step === 1 ? (
              <>
                <div className="rg-eye">Get started · 30 seconds</div>
                <h1>Tell us about your <em>restaurant</em></h1>
                <p>You can edit any of this later. The code is what your team will use to log in.</p>
              </>
            ) : (
              <>
                <div className="rg-eye">Almost there · final step</div>
                <h1>Create your <em>owner account</em></h1>
                <p>This is the master account. You can add staff and managers later from Setup.</p>
              </>
            )}
          </header>

          <form onSubmit={handleSubmit} className="rg-form" noValidate>
            {error && (
              <div className="rg-error" role="alert">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/>
                </svg>
                <span>{error}</span>
              </div>
            )}

            {/* ─── Step 1 ─── */}
            {step === 1 && (
              <div className="rg-step rg-step-enter">
                <RgField label="Restaurant Name" required>
                  <input
                    className="rg-input"
                    autoFocus
                    value={form.restaurant_name}
                    onChange={e => set('restaurant_name', e.target.value)}
                    placeholder="e.g. Himalayan Spice Restaurant"
                    required
                  />
                </RgField>

                <RgField
                  label="Restaurant Code"
                  hint="Short unique identifier — letters, numbers, and underscores only. Staff use this to log in."
                  required
                >
                  <input
                    className="rg-input rg-input--mono"
                    value={form.restaurant_code}
                    onChange={e => set('restaurant_code', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                    placeholder="himalayan"
                    autoCapitalize="none"
                    autoCorrect="off"
                    required
                  />
                  {form.restaurant_code && (
                    <div className="rg-code-preview">
                      <span className="rg-code-preview-eye">Login URL</span>
                      <span className="rg-code-preview-val">
                        vyaparpro.app/<strong>{form.restaurant_code}</strong>
                      </span>
                    </div>
                  )}
                </RgField>

                <RgField label="Branch / Location" hint="Optional — e.g. main outlet name or area.">
                  <input
                    className="rg-input"
                    value={form.branch}
                    onChange={e => set('branch', e.target.value)}
                    placeholder="e.g. Thamel, Kathmandu"
                  />
                </RgField>

                <div className="rg-actions">
                  <Link to="/login" className="rg-link-back">← Back to sign in</Link>
                  <button
                    type="button"
                    className="rg-btn rg-btn-primary"
                    onClick={goNext}
                    disabled={!step1Valid}
                  >
                    Continue
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
                  </button>
                </div>
              </div>
            )}

            {/* ─── Step 2 ─── */}
            {step === 2 && (
              <div className="rg-step rg-step-enter">
                <RgField label="Your Name" required>
                  <input
                    className="rg-input"
                    autoFocus
                    value={form.owner_name}
                    onChange={e => set('owner_name', e.target.value)}
                    placeholder="Full name"
                    required
                  />
                </RgField>

                <RgField label="Username" hint="Letters, numbers, and underscores only." required>
                  <input
                    className="rg-input rg-input--mono"
                    type="text"
                    value={form.username}
                    onChange={e => set('username', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                    placeholder="e.g. himalayan_owner"
                    autoCapitalize="none"
                    autoCorrect="off"
                    required
                  />
                </RgField>

                <div className="rg-row">
                  <RgField label="Password" required>
                    <div className="rg-input-wrap">
                      <input
                        className="rg-input"
                        type={showPw ? 'text' : 'password'}
                        value={form.password}
                        onChange={e => set('password', e.target.value)}
                        placeholder="Min 6 characters"
                        required
                        minLength={6}
                      />
                      <button
                        type="button"
                        className="rg-input-eye"
                        onClick={() => setShowPw(s => !s)}
                        aria-label={showPw ? 'Hide password' : 'Show password'}
                      >
                        {showPw ? (
                          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M2 2l20 20"/><path d="M6.7 6.7C4.6 8.3 3 10.6 2 12c2 4 6 7 10 7 1.6 0 3.1-.4 4.5-1.1"/><path d="M9.5 4.5A10 10 0 0 1 12 4c5 0 9 4 10 8-.5 1.4-1.5 2.7-2.7 3.7"/><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2"/></svg>
                        ) : (
                          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z"/><circle cx="12" cy="12" r="3"/></svg>
                        )}
                      </button>
                    </div>
                    {form.password && (
                      <div className={`rg-pw-meter rg-pw-meter--${pwStrength}`}>
                        <span /><span /><span /><span />
                        <em>
                          {pwStrength <= 1 ? 'Weak' :
                           pwStrength === 2 ? 'Okay' :
                           pwStrength === 3 ? 'Strong' : 'Very strong'}
                        </em>
                      </div>
                    )}
                  </RgField>

                  <RgField label="Confirm" required>
                    <div className="rg-input-wrap">
                      <input
                        className={`rg-input ${form.confirm && form.password !== form.confirm ? 'is-bad' : ''}`}
                        type={showConfirm ? 'text' : 'password'}
                        value={form.confirm}
                        onChange={e => set('confirm', e.target.value)}
                        placeholder="Repeat password"
                        required
                      />
                      <button
                        type="button"
                        className="rg-input-eye"
                        onClick={() => setShowConfirm(s => !s)}
                        aria-label={showConfirm ? 'Hide password' : 'Show password'}
                      >
                        {showConfirm ? (
                          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M2 2l20 20"/><path d="M6.7 6.7C4.6 8.3 3 10.6 2 12c2 4 6 7 10 7 1.6 0 3.1-.4 4.5-1.1"/></svg>
                        ) : (
                          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z"/><circle cx="12" cy="12" r="3"/></svg>
                        )}
                      </button>
                    </div>
                    {form.confirm && form.password === form.confirm && (
                      <div className="rg-match">
                        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                        Match
                      </div>
                    )}
                  </RgField>
                </div>

                <div className="rg-actions">
                  <button
                    type="button"
                    className="rg-link-back"
                    onClick={() => setStep(1)}
                  >← Back</button>
                  <button
                    type="submit"
                    className="rg-btn rg-btn-primary"
                    disabled={loading || !step2Valid}
                  >
                    {loading ? (
                      <>
                        <span className="rg-spinner" aria-hidden="true" />
                        Creating restaurant…
                      </>
                    ) : (
                      <>
                        Create account
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            <p className="rg-foot">
              Already running a restaurant on Vyapar?{' '}
              <Link to="/login">Sign in instead</Link>
            </p>
          </form>
        </div>
      </main>
    </div>
  );
}

/* Field wrapper — label + hint + error slot */
function RgField({ label, hint, required, children }) {
  return (
    <label className="rg-field">
      <span className="rg-field-label">
        {label}
        {required && <em className="rg-required" aria-hidden="true">*</em>}
      </span>
      {children}
      {hint && <span className="rg-field-hint">{hint}</span>}
    </label>
  );
}
